import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, CalendarPlus, Check, ExternalLink, Loader2, MoreHorizontal, Pencil, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import {
  MEETING_ACTION_HINTS, MEETING_ACTION_LABELS, MEETING_HISTORY_LABELS, MEETING_STATUS_LABELS, MEETING_STATUS_TONES, PARTICIPANT_ROLE_LABELS, PRESENCE_LABELS,
  cleanMinutes, formatDay, formatMoment, governanceError, groupMeetingHistory, meetingKindLabel,
} from "../lib/governance";
import { formatDateTime } from "../lib/tickets";
import { getActiveZone, utcToLocal, zoneName } from "../lib/time";
import { Chip, DecisionState, Field, LinkList } from "../components/governance/GovernanceBits";
import LinkedActions from "../components/governance/LinkedActions";
import MeetingFiles from "../components/governance/MeetingFiles";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuContent as DropdownMenuContent, ResponsiveDropdownMenuItem as DropdownMenuItem,
  ResponsiveDropdownMenuTrigger as DropdownMenuTrigger,
} from "../components/ui/responsive-dropdown-menu";

// « 23/10/2026, de 22:22 à 22:55 » for a run of edits within one day (one moment if within the same minute), both full moments otherwise.
function formatSpan(from, to) {
  const [a, b] = [formatDateTime(from), formatDateTime(to)];
  if (a === b) return b;
  return a.slice(0, 10) === b.slice(0, 10) ? `${a.slice(0, 10)}, de ${a.slice(11)} à ${b.slice(11)}` : `Du ${a} au ${b}`;
}

function Block({ title, children, action, testid }) {
  return (
    <Card className="p-4 sm:p-5 space-y-3" data-testid={testid}>
      <div className="flex items-center justify-between gap-2"><h2 className="text-base font-semibold text-slate-900">{title}</h2>{action}</div>
      {children}
    </Card>
  );
}

function ActionDialog({ action, meeting, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const needsReason = action === "cancel";
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/governance/meetings/${meeting.id}/${action}`, { expected_version: meeting.version, ...(needsReason ? { reason: reason.trim() } : {}) });
      toast.success(action === "hold" ? "Réunion ouverte" : action === "close" ? "Compte rendu clos" : "Réunion annulée");
      onDone(data);
    } catch (e) {
      setError(governanceError(e, "Impossible de continuer. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
      <DialogContent data-testid={`meeting-dialog-${action}`}>
        <DialogHeader><DialogTitle>{MEETING_ACTION_LABELS[action]} — {meeting.number}</DialogTitle><DialogDescription>{MEETING_ACTION_HINTS[action]}</DialogDescription></DialogHeader>
        {needsReason && <div className="space-y-1.5"><Label htmlFor="mt-reason">Motif</Label>
          <Textarea id="mt-reason" rows={3} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="meeting-reason" /></div>}
        {error && <p className="text-sm text-red-600" role="alert" data-testid="meeting-action-error">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Retour</Button>
          <Button variant={needsReason ? "destructive" : "default"} onClick={submit} disabled={busy || (needsReason && reason.trim().length < 3)} data-testid="meeting-action-submit">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{MEETING_ACTION_LABELS[action]}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const SAVE_IDLE_MS = 3000; // a pause in the typing
const SAVE_EVERY_MS = 30000; // at most twice a minute while writing on: each save is one line of the journal and of the audit

// Minutes: written while the meeting is open, frozen once closed — then only dated addenda are added. They save on their own (after a pause
// in the typing, at most every 30 s, and at once when the field is left or the page is), version-checked like every write. What is typed is
// never replaced by an answer of the server (a file attached, a presence noted…); a text changed elsewhere meanwhile becomes a conflict to
// settle, never a silent overwrite either way.
function Minutes({ meeting, onData }) {
  const open = meeting.status === "held" && meeting.capabilities.edit;
  const [text, setTextState] = useState(meeting.minutes || "");
  const [saveState, setSaveState] = useState(null); // saving | saved | error
  const [conflict, setConflict] = useState(null); // the text the server holds, changed elsewhere while ours was not saved
  const [addendum, setAddendum] = useState("");
  const [busy, setBusy] = useState(false);
  const textRef = useRef(text);
  const baseRef = useRef(meeting.minutes || ""); // the server text ours started from
  const versionRef = useRef(meeting.version);
  const conflictRef = useRef(null);
  const saving = useRef(false);
  const again = useRef(false);
  const timer = useRef(null);
  const lastSave = useRef(0);
  const openRef = useRef(open);
  openRef.current = open;
  versionRef.current = Math.max(versionRef.current, meeting.version);

  const setText = (value) => { textRef.current = value; setTextState(value); };
  const setConflictBoth = (value) => { conflictRef.current = value; setConflict(value); };
  const unsaved = () => cleanMinutes(textRef.current) !== cleanMinutes(baseRef.current);

  // Another write brought the meeting back (a file, a presence, a colleague): its minutes are taken only if nothing of ours is pending.
  useEffect(() => {
    const server = meeting.minutes || "";
    if (server === baseRef.current || saving.current) return;
    const untouched = !unsaved();
    if (untouched || cleanMinutes(textRef.current) === cleanMinutes(server)) {
      baseRef.current = server;
      if (untouched) setText(server);
      setConflictBoth(null);
    } else {
      setConflictBoth(server);
    }
  }, [meeting.minutes]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useCallback(async () => {
    clearTimeout(timer.current);
    if (!openRef.current || conflictRef.current !== null || !unsaved()) return;
    if (saving.current) { again.current = true; return; }
    saving.current = true;
    setSaveState("saving");
    let saved = false;
    try {
      const { data } = await api.patch(`/governance/meetings/${meeting.id}`, { expected_version: versionRef.current, minutes: textRef.current });
      baseRef.current = data.minutes || "";
      versionRef.current = data.version;
      lastSave.current = Date.now();
      saved = true;
      setSaveState("saved");
      onData(data);
    } catch (e) {
      if (e?.response?.data?.detail?.code === "version_conflict") {
        // Something else was written since (often a file attached meanwhile): read again, and save again if the minutes were not touched.
        try {
          const { data: fresh } = await api.get(`/governance/meetings/${meeting.id}`);
          versionRef.current = fresh.version;
          if ((fresh.minutes || "") === baseRef.current) again.current = true;
          else { setConflictBoth(fresh.minutes || ""); setSaveState(null); }
          onData(fresh);
        } catch {
          setSaveState("error");
        }
      } else {
        setSaveState("error");
        toast.error(governanceError(e, "Enregistrement du compte rendu impossible. Votre texte reste à l'écran : réessayez."));
      }
    } finally {
      saving.current = false;
      const now = again.current;
      again.current = false;
      if (now) save();
      else if (saved && unsaved()) schedule(); // eslint-disable-line no-use-before-define
    }
  }, [meeting.id, onData]); // eslint-disable-line react-hooks/exhaustive-deps

  const schedule = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(save, Math.max(SAVE_IDLE_MS, lastSave.current + SAVE_EVERY_MS - Date.now()));
  };
  const type = (value) => {
    setText(value);
    setSaveState(null);
    schedule();
  };
  const keepMine = () => {
    baseRef.current = conflictRef.current;
    setConflictBoth(null);
    save();
  };
  const takeTheirs = () => {
    baseRef.current = conflictRef.current;
    setText(conflictRef.current);
    setConflictBoth(null);
  };

  // Leaving with something not yet saved: the browser asks first; leaving the page within the CRM saves what is pending.
  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (!saving.current && conflictRef.current === null && !(openRef.current && unsaved())) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      save();
    };
  }, [save]); // eslint-disable-line react-hooks/exhaustive-deps

  const pending = open && !conflict && cleanMinutes(text) !== cleanMinutes(baseRef.current);
  const status = conflict !== null ? "Non enregistré — choisissez la version à garder"
    : saveState === "saving" ? "Enregistrement…"
    : saveState === "error" ? "Non enregistré — réessayez"
    : pending ? "Modifications en attente d'enregistrement automatique"
    : "Enregistré";
  const addAddendum = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/governance/meetings/${meeting.id}/addenda`, { expected_version: meeting.version, text: addendum.trim() });
      onData(data);
      setAddendum("");
      toast.success("Addendum ajouté");
    } catch (e) {
      toast.error(governanceError(e, "Impossible d'ajouter l'addendum."));
    } finally {
      setBusy(false);
    }
  };
  if (!open && !meeting.minutes && !meeting.capabilities.addendum) return null;
  return (
    <Block title="Compte rendu" testid="meeting-minutes">
      {open ? (
        <>
          <Textarea rows={10} maxLength={20000} value={text} onChange={(e) => type(e.target.value)} onBlur={save} placeholder="Ce qui a été dit, décidé, et par qui…" aria-label="Compte rendu" data-testid="minutes-text" />
          {conflict !== null && (
            <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm" role="alert" data-testid="minutes-conflict">
              <p className="flex items-start gap-1.5 font-medium text-amber-900">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" aria-hidden="true" />Le compte rendu a été modifié ailleurs pendant que vous écriviez. Votre texte est conservé dans le champ, rien n'a été écrasé. Version enregistrée :
              </p>
              <p className="max-h-32 overflow-y-auto whitespace-pre-wrap break-words rounded border border-amber-200 bg-white px-2 py-1.5 text-slate-800" aria-label="Version enregistrée du compte rendu">
                {conflict || <span className="text-slate-400">(compte rendu vide)</span>}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" variant="outline" onClick={takeTheirs} data-testid="minutes-take">Reprendre la version enregistrée</Button>
                <Button type="button" size="sm" variant="ghost" onClick={keepMine} data-testid="minutes-keep">Garder mon texte</Button>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between gap-2">
            <span className={`text-xs ${saveState === "error" ? "text-red-600" : conflict !== null ? "text-amber-700" : "text-slate-500"}`} aria-live="polite" data-testid="minutes-status">{status}</span>
            <Button size="sm" variant="outline" onClick={save} disabled={conflict !== null || (!pending && saveState !== "error")} data-testid="minutes-save">
              {saveState === "saving" && <Loader2 size={14} className="mr-1.5 animate-spin" />}Enregistrer maintenant
            </Button>
          </div>
        </>
      ) : <p className="text-sm text-slate-800 whitespace-pre-wrap" data-testid="minutes-readonly">{meeting.minutes}</p>}
      {meeting.addenda.length > 0 && (
        <div className="space-y-2 border-t border-slate-100 pt-3" data-testid="meeting-addenda">
          <h3 className="text-xs uppercase tracking-wide text-slate-500">Addenda</h3>
          {meeting.addenda.map((a) => <div key={a.id} className="border-l-2 border-slate-200 pl-3"><p className="text-sm text-slate-800 whitespace-pre-wrap">{a.text}</p><p className="text-xs text-slate-500">{formatDateTime(a.at)}{a.author ? ` · ${a.author}` : ""}</p></div>)}
        </div>
      )}
      {meeting.capabilities.addendum && (
        <div className="space-y-2 border-t border-slate-100 pt-3">
          <Label htmlFor="mt-addendum">Ajouter un addendum</Label>
          <Textarea id="mt-addendum" rows={3} maxLength={4000} value={addendum} onChange={(e) => setAddendum(e.target.value)} placeholder="Une précision ou une correction, datée et signée de votre nom." data-testid="addendum-text" />
          <div className="flex justify-end"><Button size="sm" onClick={addAddendum} disabled={busy || addendum.trim().length < 3} data-testid="addendum-submit">Ajouter</Button></div>
        </div>
      )}
    </Block>
  );
}

export default function GovernanceMeeting() {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [meeting, setMeeting] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [dialog, setDialog] = useState(null);

  // Answers can cross (a file attached while the minutes save): the meeting never steps back to an older version than the one shown.
  const apply = useCallback((data) => setMeeting((shown) => (!shown || shown.id !== data.id || data.version >= shown.version ? data : shown)), []);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/governance/meetings/${meetingId}`);
      setMeeting(data);
      setPhase("ready");
    } catch (e) {
      setPhase(e?.response?.status === 404 ? "missing" : "error");
    }
  }, [meetingId]);
  useEffect(() => { setPhase("loading"); load(); }, [load]);

  if (phase === "loading") return <div className="space-y-3"><Skeleton className="h-8 w-72" /><Skeleton className="h-40 w-full" /></div>;
  if (phase === "missing") {
    return (
      <Card className="p-8 text-center space-y-2" data-testid="meeting-missing">
        <h2 className="font-medium text-slate-900">Réunion introuvable</h2>
        <p className="text-sm text-slate-500">Elle n'existe pas, ou vous n'y avez pas accès.</p>
        <Button asChild variant="outline"><Link to="/gouvernance/reunions">Retour aux réunions</Link></Button>
      </Card>
    );
  }
  if (phase === "error") {
    return (
      <Card className="p-6 text-center space-y-3" data-testid="meeting-error">
        <AlertTriangle className="mx-auto text-amber-500" size={20} />
        <p className="text-sm text-slate-700">Impossible de charger cette réunion.</p>
        <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
      </Card>
    );
  }

  const cap = meeting.capabilities;
  const canDelete = meeting.status === "planned" && hasPermission(user, "governance:delete");
  const primary = cap.actions.includes("hold") ? "hold" : cap.actions.includes("close") ? "close" : null;
  const done = (data) => { setDialog(null); apply(data); };
  const setPresence = async (index, presence) => {
    const participants = meeting.participants.map((p, i) => ({ type: p.type, ...(p.type === "external" ? { name: p.name } : { id: p.id }), role: p.role, presence: i === index ? presence : p.presence }));
    try {
      const { data } = await api.patch(`/governance/meetings/${meeting.id}`, { expected_version: meeting.version, participants });
      apply(data);
    } catch (e) {
      toast.error(governanceError(e, "Impossible de noter la présence."));
    }
  };
  const downloadCalendar = async () => {
    try {
      const { data } = await api.get(`/governance/meetings/${meeting.id}/calendar.ics`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${meeting.number}.ics`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Impossible de préparer l'invitation. Réessayez.");
    }
  };
  const patchLinks = async (links) => {
    try {
      const { data } = await api.patch(`/governance/meetings/${meeting.id}`, { expected_version: meeting.version, links: links.map((l) => ({ kind: l.kind, ref_id: l.ref_id })) });
      apply(data);
    } catch (e) {
      toast.error(governanceError(e, "Impossible de retirer ce lien."));
    }
  };

  return (
    <div className="space-y-4" data-testid="meeting-page">
      <div>
        <Link to="/gouvernance/reunions" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft size={14} />Réunions</Link>
        <div className="mt-1 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-slate-500 tabular-nums" data-testid="meeting-number">{meeting.number} · {meetingKindLabel(meeting.kind)}</p>
            <h1 className="font-display font-bold text-2xl text-slate-900 break-words" data-testid="meeting-title">{meeting.title}</h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <Chip label={MEETING_STATUS_LABELS[meeting.status]} tone={MEETING_STATUS_TONES[meeting.status]} testid="meeting-state" />
              <span className="text-sm text-slate-600">{formatMoment(meeting.starts_at)}</span>
              {meeting.restricted && <Chip label="Restreinte" tone="slate" testid="meeting-restricted" />}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {primary && <Button onClick={() => setDialog(primary)} data-testid={`meeting-${primary}`}><Check size={15} className="mr-1.5" />{MEETING_ACTION_LABELS[primary]}</Button>}
            {cap.edit && <Button asChild variant="outline" data-testid="meeting-edit"><Link to={`/gouvernance/reunions/${meeting.id}/modifier`}><Pencil size={14} className="mr-1.5" />Modifier</Link></Button>}
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="outline" size="icon" aria-label="Autres actions" data-testid="meeting-more"><MoreHorizontal size={16} /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={downloadCalendar} data-testid="meeting-ics"><CalendarPlus size={14} className="mr-2" />Ajouter à mon agenda (.ics)</DropdownMenuItem>
                {cap.actions.includes("cancel") && <DropdownMenuItem onClick={() => setDialog("cancel")} className="text-red-600" data-testid="meeting-cancel">Annuler la réunion</DropdownMenuItem>}
                {canDelete && <DropdownMenuItem onClick={() => setDialog("delete")} className="text-red-600 focus:text-red-700" data-testid="meeting-delete">Supprimer la réunion…</DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_20rem] items-start">
        <div className="space-y-4 min-w-0">
          {meeting.agenda.length > 0 && (
            <Block title="Ordre du jour" testid="meeting-agenda">
              <ol className="space-y-2 list-decimal list-inside">
                {meeting.agenda.map((a) => <li key={a.id} className="text-sm text-slate-900" data-testid={`agenda-item-${a.id}`}><span className="font-medium">{a.title}</span>{a.note && <span className="block ml-5 text-slate-600 whitespace-pre-wrap">{a.note}</span>}</li>)}
              </ol>
            </Block>
          )}
          <Minutes meeting={meeting} onData={apply} />
          <Block title="Décisions prises" testid="meeting-decisions"
            action={cap.propose_decision && <Button asChild size="sm" variant="outline" data-testid="meeting-decision-new"><Link to={`/gouvernance/decisions/nouvelle?reunion=${meeting.id}`}><Plus size={14} className="mr-1.5" />Proposer une décision</Link></Button>}>
            {meeting.decisions.length === 0 ? <p className="text-sm text-slate-500" data-testid="meeting-decisions-empty">Aucune décision rattachée à cette réunion.</p> : (
              <ul className="divide-y divide-slate-100">
                {meeting.decisions.map((d) => (
                  <li key={d.id} className="py-2 flex flex-wrap items-center justify-between gap-2" data-testid={`meeting-decision-${d.number}`}>
                    <Link to={`/gouvernance/decisions/${d.id}`} className="min-w-0 text-sm text-slate-900 hover:text-jamora"><span className="text-xs text-slate-500 mr-2 tabular-nums">{d.number}</span>{d.title}</Link>
                    <DecisionState decision={d} testid={`meeting-decision-state-${d.number}`} />
                  </li>
                ))}
              </ul>
            )}
          </Block>
          <LinkedActions base={`/governance/meetings/${meeting.id}`} tasks={meeting.tasks} canLink={cap.link_tasks} reference={meeting.number} onData={apply} />
          <MeetingFiles meeting={meeting} canAttach={!!cap.files} onData={apply} />
          {meeting.links.length > 0 && (
            <Block title="Liens" testid="meeting-links">
              <LinkList links={meeting.links} onRemove={cap.link_tasks ? (l) => patchLinks(meeting.links.filter((x) => x !== l)) : undefined} />
            </Block>
          )}
          <Block title="Historique" testid="meeting-history">
            <ol className="space-y-2">
              {groupMeetingHistory(meeting.history).map((h) => (
                <li key={h.id} className="border-l-2 border-slate-200 pl-3" data-testid="meeting-history-entry">
                  <div className="text-sm text-slate-900">{MEETING_HISTORY_LABELS[h.kind] || h.kind}{h.count > 1 && <span className="text-slate-500"> · {h.count} enregistrements</span>}</div>
                  <div className="text-xs text-slate-500">{h.count > 1 ? formatSpan(h.first_at, h.at) : formatDateTime(h.at)}{h.actor_name ? ` · ${h.actor_name}` : ""}</div>
                  {h.reason && <div className="text-xs text-slate-600">Motif : {h.reason}</div>}
                </li>
              ))}
            </ol>
          </Block>
        </div>

        <div className="space-y-4 lg:sticky lg:top-4">
          <Card className="p-4" data-testid="meeting-side">
            <h2 className="text-base font-semibold text-slate-900 mb-1">Fiche</h2>
            <dl className="divide-y divide-slate-100">
              <Field label="Quand">{formatMoment(meeting.starts_at)}{meeting.ends_at ? ` → ${utcToLocal(meeting.ends_at, getActiveZone()).split("T")[1]}` : ""}
                <span className="block text-xs text-slate-500" data-testid="meeting-zone">Heure de {zoneName(getActiveZone())}</span></Field>
              {meeting.place && <Field label="Lieu">{meeting.place}</Field>}
              {meeting.video_url && <Field label="Visio"><a className="text-jamora hover:underline inline-flex items-center gap-1 break-all" href={meeting.video_url} target="_blank" rel="noopener noreferrer" data-testid="meeting-video">Ouvrir le lien<ExternalLink size={12} aria-hidden="true" /></a></Field>}
              <Field label="Créée par">{meeting.author.name || "—"}</Field>
              <Field label="Le">{formatDay(meeting.created_at)}</Field>
            </dl>
          </Card>
          <Card className="p-4 space-y-2" data-testid="meeting-participants">
            <h2 className="text-base font-semibold text-slate-900">Participants</h2>
            {meeting.participants.length === 0 ? <p className="text-sm text-slate-500">Aucun participant renseigné.</p> : (
              <ul className="divide-y divide-slate-100">
                {meeting.participants.map((p, i) => (
                  <li key={`${p.type}:${p.id || p.name}`} className="py-2 flex items-center justify-between gap-2" data-testid={`participant-${i}`}>
                    <div className="min-w-0"><div className="text-sm text-slate-900 truncate">{p.name || "—"}</div>{p.role !== "participant" && <div className="text-xs text-slate-500">{PARTICIPANT_ROLE_LABELS[p.role]}</div>}</div>
                    {cap.edit ? (
                      <Select value={p.presence} onValueChange={(v) => setPresence(i, v)}>
                        <SelectTrigger className="h-8 w-28 text-xs" aria-label={`Présence de ${p.name}`} data-testid={`presence-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.entries(PRESENCE_LABELS).map(([id, label]) => <SelectItem key={id} value={id}>{label}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : <span className="text-xs text-slate-600 shrink-0">{PRESENCE_LABELS[p.presence]}</span>}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
      {dialog && dialog !== "delete" && <ActionDialog action={dialog} meeting={meeting} onClose={() => setDialog(null)} onDone={done} />}
      <DeleteWithReasonDialog
        target={dialog === "delete" ? meeting : null} testId="delete-governance" onClose={() => setDialog(null)}
        title={`Supprimer la réunion ${meeting.number} ?`}
        description="Elle va dans la corbeille et se restaure pendant 30 jours. Une réunion tenue, avec compte rendu, fichier, action ou décision, se clôt ou s'annule à la place."
        onConfirm={async (reason) => {
          try {
            await api.delete(`/governance/meetings/${meeting.id}`, { data: { reason } });
            toast.success("Mis à la corbeille");
            navigate("/gouvernance/reunions");
          } catch (e) {
            toast.error(governanceError(e, "Impossible de supprimer cet élément."));
            setDialog(null);
          }
        }}
      />
    </div>
  );
}
